import React, { useRef, useEffect, useContext, useState } from "react";

// Import ThoughtSpot SDK
import {
    init,
    SageEmbed,
    Action,
    EmbedEvent,
    AuthType,
    HostEvent,
    SpotterEmbed,
    RuntimeFilterOp,
    CustomActionsPosition,
    CustomActionTarget,
} from "@thoughtspot/visual-embed-sdk";

import { Context } from "../../../_context/Store";

import ActiveIcon from "../../../styles/img/svg/ActiveIcon";
import InActiveIcon from "../../../styles/img/svg/InActiveIcon";
import DatabaseIcon from "../../../styles/img/svg/DatabaseIcon";
import ExpandIcon from "../../../styles/img/svg/ExpandIcon";
import SettingIcon from "../../../styles/img/svg/SettingIcon";

import { THOUGHTSPOT_CONFIG } from "../../../_redux/_constants/insights.constant";

import AiInstructionForm from "./forms/AiInstructionForm";

const getVisibleCount = () => (window.innerWidth >= 1024 ? 3 : 2);

export default function AiChatWithModel() {
    const chatRef = useRef(null);
    const mountedRef = useRef(true); // Track mount state

    const [state, dispatch] = useContext(Context);
    const [visibleCount, setVisibleCount] = useState(getVisibleCount());

    const worksheetId = THOUGHTSPOT_CONFIG.THOUGHTSPOTTER_WORKSHEET_ID;

    if (!worksheetId) {
        console.error("THOUGHTSPOTTER_WORKSHEET_ID not set");
        return <div>Configuration missing</div>;
    }

    const listOfActivatedSource = [
        { label: "User Data", active: true },
        { label: "Dealer Area Data", active: true },
        { label: "Dealer Master Data", active: true },
        { label: "Customer Data", active: true },
        { label: "Participant Job Role Data", active: true },

        { label: "Participant Master Data", active: true },

        { label: "Participant Engagement Data", active: false },
        { label: "Participant Points Bank Data ", active: false },
        { label: "Campaign Master Data", active: false },
        { label: "Campaign Metrics Data", active: false },
        { label: "Campaign Rewards Data", active: false },
        { label: "Voucher Master Data", active: false },
        { label: "Participant Voucher Purchase Data", active: false },
    ];

    useEffect(() => {
        if (!chatRef.current) return;

        const chatEmbed = new SpotterEmbed(chatRef.current, {
            worksheetId: worksheetId,
            frameParams: { width: "100%", height: "100%" },
            // optional: prefill a question
            initialQuestion: "Show total sales by region",
            // optional: hide top bar
            hideAppBar: false,
            hideFooter: true,
            customizations: {
                content: {
                    strings: {
                        Spotter: "SherBot",
                        SpotIQ: "SherBot",
                        Thoughtspot: "Sherbot",
                    },
                },
                style: {
                    customCSS: {
                        variables: {
                            "--ts-var-button--primary-background": "#010001",
                            "--ts-var-button--primary--hover-background":
                                "#000000ff",
                        },
                        rules_UNSTABLE: {
                            ".embed-module__footerWrapper": {
                                display: "none",
                            },
                            ".chat-input-module__chatInputContainer": {
                                "min-width": "auto",
                                padding: 0,
                            },
                            ".chat-module__instructionsButton": {
                                display: "none",
                            },
                            ".query-suggestions-module__querySuggestionsContainer":
                                {
                                    width: "auto",
                                },
                            ".collapsible-item-response-module__iconContainer":
                                { display: "none !important" },
                        },
                    },
                },
                iconSpriteUrl:
                    "https://cdn.jsdelivr.net/gh/thoughtspot/custom-css-demo/alternate-spotter-icon.svg",
            },
            disableSourceSelection: true,
            hideSourceSelection: false,
            hideSampleQuestions: true, //hide showing sample questions
            hiddenActions: [
                Action.InConversationTraining,
                Action.PreviewDataSpotter,
                Action.SyncToSheets,
                Action.SyncToOtherApps,
                Action.ManagePipelines,
            ],
        });

        const onInit = () => {
            if (!mountedRef.current) return;
        };

        const onLoad = () => {
            if (!mountedRef.current) return;
        };

        const onError = (err) => {
            if (!mountedRef.current) return;
        };

        chatEmbed
            .on(EmbedEvent.Init, onInit)
            .on(EmbedEvent.Load, onLoad)
            .on(EmbedEvent.Error, onError)
            .render();

        return () => {
            // Mark component as unmounted
            mountedRef.current = false;

            // Safe cleanup of container
            if (chatRef.current) {
                chatRef.current.innerHTML = "";
            }
        };
    }, []);

    useEffect(() => {
        const handleResize = () => {
            setVisibleCount(getVisibleCount());
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const showActiveSourcesList = () => {
        dispatch({
            type: "FOOTER_CONTENT_MODAL",
            payload: {
                show: true,
                children: (
                    <>
                        <div>
                            <h6 className="sb-absolute sb-top-0 px-2">
                                Data source list
                            </h6>

                            <div className="sb-flex-row sb-flex-wrap sb-gap-1 sb-items-center sb-mt-4">
                                {listOfActivatedSource
                                    .sort((a, b) => b.active - a.active)
                                    .map((data, index) => (
                                        <div
                                            key={index}
                                            className="badge rounded-pill bg-light text-dark sb-flex-row sb-gap-1 sb-items-center "
                                        >
                                            <DatabaseIcon />
                                            {data.label}
                                            {data.active ? (
                                                <ActiveIcon />
                                            ) : (
                                                <InActiveIcon />
                                            )}
                                        </div>
                                    ))}
                            </div>
                        </div>
                    </>
                ),
            },
        });
    };

    const showAIInstruction = () => {
        dispatch({
            type: "LARGE_MODAL",
            payload: {
                show: true,
                title: "Sherbot AI Instructions",
                subTitle: "",
                backdrop: "static",
                centered: true,
                children: (
                    <>
                        <AiInstructionForm />
                    </>
                ),
            },
        });
    };

    return (
        <>
            <div className="sb-flex-row  sb-justify-center">
                <div className="sb-data-source-container sb-flex-row  sb-gap-1 sb-items-center sb-absolute sb-top-2 sb-justify-between">
                    <div className="sb-flex-row sb-flex-wrap sb-gap-1 sb-items-center">
                        {listOfActivatedSource
                            .slice(0, visibleCount)
                            .map((data, index) => (
                                <div
                                    key={index}
                                    className="badge rounded-pill bg-light text-dark sb-flex-row sb-gap-1 sb-items-center "
                                >
                                    <DatabaseIcon />
                                    {data.label}
                                    {data.active ? (
                                        <ActiveIcon />
                                    ) : (
                                        <InActiveIcon />
                                    )}
                                </div>
                            ))}
                    </div>

                    {listOfActivatedSource.length >= visibleCount && (
                        <div
                            className="sb-cursor-pointer"
                            onClick={showActiveSourcesList}
                        >
                            <ExpandIcon />
                        </div>
                    )}

                    <div
                        className="sb-cursor-pointer"
                        onClick={showAIInstruction}
                    >
                        <SettingIcon />
                    </div>
                </div>
            </div>

            <div
                ref={chatRef}
                style={{
                    width: "100%",
                    height: "90vh",
                }}
            />
        </>
    );
}
